import { useRef, useEffect, useState } from "react";
import {
  Anchor,
  Blockquote,
  Button,
  Box,
  Text,
  useMantineTheme,
} from "@mantine/core";
import ItineraryBuilder from "./ItineraryBuilder/ItineraryBuilder";
import TripInformation from "./TripInformation";
import ContactInformation from "./ContactInformation";
import OtherItineraryBuilder from "./ItineraryBuilder/OtherItineraryBuilder";
import { CircleCheckIcon } from "./icons";
import { toSnakeCase } from "../get-a-quote/helpers/GetAQuoteHelpers";
import { sendTripData } from "./helpers/Api";
import { useStickyFooter } from "./hooks/useStickyFooter";

export default function Sidebar({
  step,
  isOther,
  tripType,
  setTripType,
  updateLocation,
  locations,
  setLocations,
  formData,
  updateFormField,
  tripTypeTitle,
  stopOrderIsValid,
  hasAllLocations,
  onNext,
  isValidEmail,
  tripInfoData,
  contactInfoData,
  isMobile,
}) {
  const theme = useMantineTheme();
  const sideBarRef = useRef(null);
  const { nextButtonAnchorRef, isAtBottom } = useStickyFooter(isMobile);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const snakeCaseFormData = toSnakeCase(formData);
    const data = {
      locations,
      tripInfoData,
      contactInfoData,
      ...snakeCaseFormData,
      tripType,
    };

    setLoading(true);
    const result = await sendTripData(data);
    if (!result.success) {
      setLoading(false);
    }
  };

  const disableNext = () => {
    if (step === 2) {
      if (!isOther) {
        if (!stopOrderIsValid) return true;
        if (formData.specialRequests) return false;
        if (hasAllLocations) return false;
        return true;
      } else {
        if (formData.otherDate && formData.duration && formData.city) {
          return false;
        }
        return true;
      }
    }
    if (step === 3) {
      if (
        formData.pax != null &&
        formData.pax !== "" &&
        formData.groupType &&
        formData.busType
      ) {
        return false;
      }
      return true;
    }

    if (step === 4) {
      if (
        formData.firstName &&
        formData.lastName &&
        formData.email &&
        formData.phone &&
        formData.phoneIsValid
      ) {
        return false;
      }
      return true;
    }
    return true;
  };

  useEffect(() => {
    sideBarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const isDisabled = disableNext();

  return (
    <Box
      ref={sideBarRef}
      style={{
        width: "100%",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box style={{ flex: 1 }}>
        {step === 2 && !isOther && (
          <ItineraryBuilder
            tripType={tripType}
            tripTypeTitle={tripTypeTitle()}
            updateLocation={updateLocation}
            locations={locations}
            setLocations={setLocations}
            formData={formData}
            updateFormField={updateFormField}
          />
        )}

        {step === 2 && isOther && (
          <OtherItineraryBuilder
            formData={formData}
            updateLocation={updateLocation}
            updateFormField={updateFormField}
          />
        )}

        {step === 3 && (
          <TripInformation
            formData={formData}
            updateFormField={updateFormField}
          />
        )}

        {step === 4 && (
          <ContactInformation
            formData={formData}
            updateFormField={updateFormField}
            isValidEmail={isValidEmail()}
          />
        )}

        {tripType !== "one_way" && step < 4 && (
          <Blockquote
            w="100%"
            mt="xl"
            styles={{
              root: {
                backgroundColor: theme.colors.primary[0],
                borderLeft: "none",
                borderRadius: "6px",
                padding: theme.spacing.md,
                color: theme.colors.primary[6],
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "20px",
              },
            }}
          >
            <Box style={{ flexShrink: 0 }}>
              <CircleCheckIcon
                stroke={1.5}
                width={17}
                height={17}
                style={{ color: theme.colors.primary[6] }}
              />
            </Box>
            <Text size="sm" fw={500}>
              {tripType === "round_trip" ? (
                <>
                  If you need to customize your trip or add stops on your return
                  leg, please go back and select "custom route" as the trip type
                  or{" "}
                  <Anchor
                    component="button"
                    onClick={() => setTripType("custom")}
                  >
                    Click Here
                  </Anchor>
                </>
              ) : (
                <>Our team will follow up to provide exact pricing</>
              )}
            </Text>
          </Blockquote>
        )}

        {!stopOrderIsValid && (
          <Text size="sm" c="red" mt="md">
            Date and Times must be in chronological order
          </Text>
        )}
      </Box>

      {/* The Next button anchor */}
      <div
        ref={nextButtonAnchorRef}
        style={{
          height: "1px",
          width: "100%",
          marginTop: isMobile ? "20px" : "0px",
        }}
      />

      {isMobile ? (
        <Box
          style={
            isAtBottom
              ? {
                  width: "100%",
                }
              : {
                  position: "fixed",
                  bottom: 0,
                  width: "100vw",
                  left: "50%",
                  transform: "translateX(-50%)",

                  zIndex: 9999,
                  backgroundColor: "white",
                }
          }
        >
          <Button
            fullWidth
            size="xl"
            disabled={isDisabled}
            onClick={step === 4 ? submit : onNext}
            loading={loading}
            radius={0}
            h={60}
            style={{
              color: "white",
              backgroundColor: isDisabled ? "#E9ECEF" : theme.colors.primary[6],
              fontWeight: 600,
              border: "none",
              fontFamily: '"Avenir Next", sans-serif',
            }}
          >
            {step === 4 ? "Submit" : "Next"}
          </Button>
        </Box>
      ) : (
        <Button
          fullWidth
          mt="xl"
          size="md"
          disabled={isDisabled}
          onClick={step === 4 ? submit : onNext}
          loading={loading}
        >
          {step === 4 ? "Submit" : "Next"}
        </Button>
      )}
    </Box>
  );
}
